import { useState } from 'react';
import PageHeader from '../components/PageHeader.jsx';
import Card from '../components/Card.jsx';
import { TextField } from '../components/Field.jsx';
import RowActions from '../components/RowActions.jsx';
import { Loader, ErrorAlert, EmptyRow, StatusBadge } from '../components/States.jsx';

import useForm from '../hooks/useForm.js';
import {
  useListVendorsQuery,
  useCreateVendorMutation,
  useUpdateVendorMutation,
  useToggleVendorMutation,
  useDeleteVendorMutation,
} from '../store/apiSlice.js';
import { vendorSchema } from '../validation/schemas.js';
import { useToast } from '../context/ToastContext.jsx';
import { useReferenceData } from '../store/useReferenceData.js';
import { fmtDate, dash } from '../utils/format.js';
import { downloadCsv } from '../utils/csv.js';

const BLANK = { company: '', contact: '', address: '', gst: '', phone: '' };

export default function VendorsPage() {
  const showToast = useToast();
  const { reload: reloadReferenceData } = useReferenceData();
  const { data: vendors, isLoading: loading, error: apiError, refetch: reload } = useListVendorsQuery();
  const error = apiError?.message ?? null;

  const [createVendor] = useCreateVendorMutation();
  const [updateVendor] = useUpdateVendorMutation();
  const [toggleVendor] = useToggleVendorMutation();
  const [deleteVendor] = useDeleteVendorMutation();

  const form = useForm(BLANK, vendorSchema);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const cancelEdit = () => {
    setEditingId(null);
    form.reset(BLANK);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.validate()) return;

    setBusy(true);
    try {
      if (editingId) await updateVendor({ id: editingId, payload: form.values }).unwrap();
      else await createVendor(form.values).unwrap();
      showToast(editingId ? 'Vendor updated' : 'Vendor added');
      cancelEdit();
      await Promise.all([reload(), reloadReferenceData()]);
    } catch (err) {
      form.applyServerErrors(err.details);
      showToast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (vendor) => {
    setEditingId(vendor.id);
    form.fill({
      company: vendor.company,
      contact: vendor.contact,
      address: vendor.address,
      gst: vendor.gst,
      phone: vendor.phone,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (vendor) => {
    if (!window.confirm(`Delete "${vendor.company}"?`)) return;
    setBusy(true);
    try {
      await deleteVendor(vendor.id).unwrap();
      if (editingId === vendor.id) cancelEdit();
      showToast('Vendor deleted');
      await Promise.all([reload(), reloadReferenceData()]);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (vendor) => {
    setBusy(true);
    try {
      const updated = await toggleVendor(vendor.id).unwrap();
      showToast(updated.status === 'disabled' ? 'Vendor disabled' : 'Vendor enabled');
      await Promise.all([reload(), reloadReferenceData()]);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    downloadCsv('vendors', [
      { label: 'Sr. No', value: (vendor) => vendors.indexOf(vendor) + 1 },
      { label: 'Company', value: (vendor) => vendor.company },
      { label: 'Contact', value: (vendor) => vendor.contact },
      { label: 'Phone', value: (vendor) => vendor.phone },
      { label: 'GST', value: (vendor) => vendor.gst },
      { label: 'Address', value: (vendor) => vendor.address },
      { label: 'Status', value: (vendor) => vendor.status || 'active' },
      { label: 'Created At', value: (vendor) => vendor.createdAt || '' },
      { label: 'Last Updated', value: (vendor) => vendor.updatedAt || '' },
    ], vendors || []);
  };

  return (
    <>
      <PageHeader
        eyebrow="Partners"
        title="Vendor Management"
        subtitle="Vendors supply your stock. A vendor already used in Stock In cannot be deleted — disable it instead."
      />

      <Card title={editingId ? 'Edit Vendor' : 'Add Vendor'} tag="Vendors">
        {editingId && (
          <div className="editing-bar">
            <span>Editing “{form.values.company}”</span>
            <button type="button" className="action-btn" onClick={cancelEdit}>
              Cancel
            </button>
          </div>
        )}
        <form onSubmit={submit} noValidate>
          <div className="form-grid">
            <TextField label="Company Name" placeholder="e.g. ABC Hair Supplier" maxLength={80} {...form.fieldProps('company')} />
            <TextField label="Contact Person" placeholder="Full name" maxLength={60} {...form.fieldProps('contact')} />
            <TextField label="Phone Number" placeholder="+91 98290 11223" {...form.fieldProps('phone')} />
            <TextField label="GST Number" placeholder="08AAACA1234A1Z5" maxLength={15} hint="15 characters. Leave blank if unregistered." {...form.fieldProps('gst')} />
            <TextField label="Address" full placeholder="Street, city" maxLength={200} {...form.fieldProps('address')} />
          </div>
          <div className="btn-row">
            <button className="btn primary" type="submit" disabled={busy}>
              {editingId ? 'Update Vendor' : 'Add Vendor'}
            </button>
          </div>
        </form>
      </Card>

      <Card title="Vendor List" tag="All Vendors">
        <div className="table-toolbar">
          <button type="button" className="btn ghost" onClick={exportCsv} disabled={!vendors?.length}>
            Export CSV
          </button>
        </div>
        <ErrorAlert message={error} onRetry={reload} />
        {loading ? (
          <Loader />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="list-table">
              <thead>
                <tr>
                  <th className="sr-col">Sr. No</th>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th>GST</th>
                  <th>Address</th>
                  <th>Last Updated</th>
                  <th className="action-col">Action</th>
                </tr>
              </thead>
              <tbody>
                {!vendors?.length ? (
                  <EmptyRow colSpan={8}>Add your first vendor above</EmptyRow>
                ) : (
                  vendors.map((vendor, index) => (
                    <tr key={vendor.id} className={vendor.status === 'disabled' ? 'row-disabled' : ''}>
                      <td className="sr-col">{index + 1}</td>
                      <td>
                        {vendor.company} <StatusBadge status={vendor.status} />
                      </td>
                      <td>{dash(vendor.contact)}</td>
                      <td className="nowrap">{dash(vendor.phone)}</td>
                      <td className="nowrap">{dash(vendor.gst)}</td>
                      <td>{dash(vendor.address)}</td>
                      <td className="nowrap">{fmtDate(vendor.updatedAt)}</td>
                      <td className="action-col">
                        <RowActions
                          status={vendor.status}
                          busy={busy}
                          onEdit={() => startEdit(vendor)}
                          onDelete={() => handleDelete(vendor)}
                          onToggle={() => handleToggle(vendor)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
